import React, { useState } from "react";
import { BiLineChart } from "react-icons/bi";
import { Audio } from "react-loader-spinner";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { GetParameter, RemoveParameter } from "src/assets/functions";
import SideListView from "src/components/SideListView";
import { useStudent } from "src/contexts/student";
import EmptyListInfo from "src/modules/protocols/components/EmptyListInfo";
import api from "src/rest/api";
import PlanItem from "./Components/planitem";
import ProgressionView from "./Components/ProgressionView";
import { LPItem } from "./interfaces";
import { List } from "./styles";

/**
 * Componente que exibe a Progressão de Carga dos Treinos do Aluno
 */
const ChargeProgression: React.FC = () => {
  const currentPlan = GetParameter("plan");
  const currentWorkout = GetParameter("workout");
  const navigate = useNavigate();
  const student = useStudent();
  const [expanded, setExpanded] = useState<number[]>([]);

  // Busca os dados da API
  const { data: history, isLoading } = useQuery(["cp"], () =>
    api
      .post<LPItem[]>("/cprogress/list", { userid: student.student?.id })
      .then((res) => res.data)
      .catch(() => undefined),
  );

  /** Alterna a expansão do item */
  const toggleExpand = (index: number) => {
    setExpanded((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  /** Seleciona um plano */
  const selectPlan = (plan: string) => {
    navigate(currentPlan === plan ? "/student/progression" : `?plan=${plan}`, {
      replace: true,
    });
  };

  /** Seleciona um treino */
  const selectWorkout = (plan: string, workout: string) => {
    navigate(
      currentWorkout === workout
        ? RemoveParameter("workout")
        : `?plan=${plan}&workout=${workout}`,
      { replace: true },
    );
  };

  /** Retorna o título correto da tela */
  const getTitle = (): { title: string; subtitle: string } => {
    if (!history || !currentPlan)
      return {
        title: "Progresso Geral",
        subtitle: "Progressão de cada exercício",
      };

    const currentPlanItem = history.find((item) => item.id === currentPlan);

    if (currentPlanItem && !currentWorkout) {
      return {
        title: currentPlanItem?.title ?? "nan",
        subtitle: "Progressão do protocolo",
      };
    } else {
      const currentWorkoutItem = currentPlanItem?.workouts.find(
        (e) => e.id === currentWorkout,
      );

      return {
        title: currentWorkoutItem?.title ?? "nan",
        subtitle: "Progressão do treino",
      };
    }
  };

  return (
    <SideListView
      list={
        <div className="column flex w-full">
          <div className="py-2">
            <h2 className="px-2 text-lg font-semibold">Progressão de Carga</h2>
          </div>

          {isLoading ? (
            <div className="m-auto">
              <Audio color="#000" height={20} width={20} />
            </div>
          ) : history && history.length ? (
            <List className="overflow-auto">
              {history.map((plan, i) => (
                <PlanItem
                  key={plan.id + "_key"}
                  index={i}
                  plan={plan}
                  isExpanded={expanded.includes(i) || currentPlan === plan.id}
                  isSelected={currentPlan === plan.id && !currentWorkout}
                  onToggleExpand={() => {
                    toggleExpand(i);
                  }}
                  onSelectPlan={() => selectPlan(plan.id)}
                  onSelectWorkout={selectWorkout}
                  currentWorkout={currentWorkout}
                />
              ))}
            </List>
          ) : (
            <div className="m-auto flex items-center justify-center">
              <EmptyListInfo Icon={BiLineChart} type="Progressão" />
            </div>
          )}
        </div>
      }
      content={<ProgressionView header={getTitle()} />}
      view={currentPlan || currentWorkout ? "CONTENT" : "LIST"}
    />
  );
};

export default ChargeProgression;
